import { useGoogleLogin } from '@react-oauth/google';
import GoogleSignInButton from './GoogleSignInButton';
import { GOOGLE_CLIENT_ID } from '../../config/googleOAuthConfig';
import { getRequiredVerificationRoute } from '../../utils/verification';
import React, { useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { signupUser, socialLogin, clearError } from '../../store/slices/authSlice';
import { signupOtp } from '../../api/driverAuth';
import { getErrorMessage } from '../../utils/apiResponseUtils';
import { initialSignupVerification, signupVerificationReducer, isProofError } from '../../utils/driverSignup';
import AuthShowcase from './AuthShowcase';
import { ROUTES } from '../../config/appConfig';
import '../../styles/Auth.css';

const SignupForm = ({ role = 'driver' }) => {
  const isDriver = role === 'driver';
  const loginRoute = isDriver ? ROUTES.driverLogin : ROUTES.login;
  const dashboardRoute = isDriver ? ROUTES.driverDashboard : ROUTES.dashboard;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state.auth);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [verification, update] = useReducer(signupVerificationReducer, undefined, initialSignupVerification);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const googleLogin = useGoogleLogin({
    onSuccess: async ({ access_token }) => {
      setError('');
      const result = await dispatch(socialLogin({ provider: 'google', token: access_token, role }));
      if (result.error) { setError(String(result.payload || 'Google sign-up failed.')); return; }
      update({ type: 'reset' });
      navigate(getRequiredVerificationRoute(result.payload.user) || dashboardRoute, { replace: true, state: { from: { pathname: dashboardRoute } } });
    },
    onError: () => setError('Google sign-up was cancelled or failed. Please try again.'),
  });
  const continueWithGoogle = () => {
    if (loading) return;
    if (!GOOGLE_CLIENT_ID) { setError('Google sign-in is not configured.'); return; }
    googleLogin();
  };
  const busy = loading || verification.email.busy || verification.phone.busy;
  const normalized = { ...form, firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim().toLowerCase(), phone: form.phone.trim() };
  const validate = (contact) => {
    const next = {};
    if (!normalized.firstName) next.firstName = 'First name is required.';
    if (!normalized.lastName) next.lastName = 'Last name is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized.email)) next.email = 'Enter a valid email address.';
    if (!/^\d{10}$/.test(normalized.phone)) next.phone = 'Enter exactly 10 digits.';
    if (form.password.length < 8) next.password = 'Use at least eight characters.';
    if (form.confirmPassword !== form.password) next.confirmPassword = 'Passwords must match.';
    setErrors(contact ? { [contact]: next[contact] } : next); return next;
  };
  const change = (field, value) => {
    setForm(current => ({ ...current, [field]: value })); setErrors({}); setError('');
    if (field === 'email' || field === 'phone') update({ type: 'edit', contact: field });
  };
  const verify = async (contact, action) => {
    if (busy || verification[contact].proof) return;
    const validation = validate(contact);
    if (validation[contact]) return;
    if (contact === 'phone' && !verification.email.proof) return;
    const current = verification[contact];
    if (action === 'confirm' && (!current.requestToken || !/^\d{4,8}$/.test(current.otp))) {
      update({ contact, value: { error: 'Enter a 4-8 digit OTP after requesting a code.' } }); return;
    }
    update({ type: action === 'send' ? 'send' : 'busy', contact, value: { busy: true, error: '' } });
    setError('');
    try {
      const result = await signupOtp(action, { [contact]: normalized[contact], role: isDriver ? 'DRIVER' : 'user',
        ...(contact === 'phone' ? { emailVerificationProofToken: verification.email.proof } : {}),
        ...(action === 'confirm' ? { requestToken: current.requestToken, otp: current.otp } : {}) });
      update({ contact, type: action === 'confirm' ? 'verified' : 'sent', value: action === 'send'
        ? { requestToken: result.requestToken, busy: false, otp: '', proof: '', error: '' }
        : { proof: result.verificationProofToken, busy: false, otp: '', error: '' } });
    } catch (failure) {
      if (isProofError(failure)) { update({ type: 'reset' }); setError('Verification expired. Verify both contacts again, starting with email.'); }
      else update({ contact, value: { busy: false, error: getErrorMessage(failure, failure.message || 'Verification failed') } });
    }
  };
  const submit = async event => {
    event.preventDefault(); if (busy || Object.keys(validate()).length) return;
    if (!verification.email.proof || !verification.phone.proof) { setError('Verify email first, then mobile.'); return; }
    dispatch(clearError()); setError('');
    const { confirmPassword, ...signupData } = normalized;
    const result = await dispatch(signupUser({ ...signupData, role, emailVerificationProofToken: verification.email.proof, phoneVerificationProofToken: verification.phone.proof }));
    if (result.error) {
      if (isProofError(result.payload)) { update({ type: 'reset' }); setError('Verification expired. Verify both contacts again, starting with email.'); }
      else setError(String(result.payload || 'Could not create account.') + ' If you already have an account, sign in.');
    } else {
      update({ type: 'reset' });
      navigate(result.payload.token ? dashboardRoute : loginRoute, { replace: true, state: { message: 'Account created. Sign in to continue.' } });
    }
  };
  const verificationDetails = contact => {
    const state = verification[contact];
    return <>
      {state.error && <Alert variant="danger">{state.error}</Alert>}
      {state.requestToken && !state.proof && <Form.Group className="mb-3" controlId={contact + '-signup-otp'}>
        <Form.Label>{contact === 'email' ? 'Email' : 'Mobile'} verification OTP</Form.Label>
        <Form.Control inputMode="numeric" autoComplete="one-time-code" value={state.otp} disabled={busy}
          onChange={event => update({ contact, value: { otp: event.target.value.replace(/\D/g, '').slice(0, 8), error: '' } })} />
        <Button className="mt-2" type="button" disabled={busy} onClick={() => verify(contact, 'confirm')}>Confirm code</Button>
      </Form.Group>}
    </>;
  };
  const field = (name, label, placeholder, autoComplete) => {
    const contact = name === 'email' || name === 'phone';
    const state = contact ? verification[name] : null;
    return <>
      <Form.Group className="mb-3" controlId={'driver-signup-' + name}>
        <Form.Label>{label}</Form.Label>
        <div className={contact ? 'auth-contact-field' : undefined}>
          <Form.Control type={name.includes('assword') ? 'password' : name === 'email' ? 'email' : name === 'phone' ? 'tel' : 'text'}
            placeholder={placeholder} autoComplete={autoComplete} value={form[name]} disabled={busy} isInvalid={!!errors[name]}
            onChange={event => change(name, name === 'phone' ? event.target.value.replace(/\D/g, '').slice(0, 10) : event.target.value)} />
          {contact && <Button type="button" variant="outline-primary" className="auth-contact-verify"
            aria-label={(state.proof ? 'Verified' : state.requestToken ? 'Resend' : 'Verify') + ' ' + (name === 'email' ? 'email' : 'mobile')}
            disabled={busy || !!state.proof || (name === 'phone' && !verification.email.proof)} onClick={() => verify(name, 'send')}>
            {state.proof ? 'Verified' : state.requestToken ? 'Resend' : 'Verify'}
          </Button>}
        </div>
        {errors[name] && <div className="invalid-feedback d-block">{errors[name]}</div>}
      </Form.Group>
      {contact && verificationDetails(name)}
    </>;
  };
  return <div className={isDriver ? "auth-page driver-auth-page" : "auth-page"}><Container><Row className="justify-content-center align-items-center min-vh-100"><Col xs={12} xl={11}>
    <div className={isDriver ? "auth-layout auth-layout-driver" : "auth-layout auth-layout-customer"}>
      <AuthShowcase variant={isDriver ? "driver" : undefined} platformLabel={isDriver ? "Driver Operations Platform" : "Customer Delivery Platform"} kicker={isDriver ? "Driver registration" : "Customer registration"} title={isDriver ? "Join Redgaady as a driver" : "Start sending deliveries with less setup friction."} description={isDriver ? "Verify your email, then mobile number. Submit documents after signing in for approval." : "Set up your account once and manage deliveries, payments, and tracking from a single workspace."} features={[]} />
      <div className="auth-card auth-form-card p-4 rounded shadow">
        <div className="auth-heading-block"><span className={isDriver ? "auth-chip driver-chip-auth" : "auth-chip"}>{isDriver ? "Driver Registration" : "Customer Registration"}</span><h2 className="mb-2 fw-bold">{isDriver ? "Create your driver account" : "Create your account"}</h2><p className="text-muted mb-0">Enter your details and verify your email and mobile number to continue.</p></div>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form onSubmit={submit}>
          <Row className="gx-3">
            <Col xs={6}>{field('firstName', 'First Name', 'John', 'given-name')}</Col>
            <Col xs={6}>{field('lastName', 'Last Name', 'Doe', 'family-name')}</Col>
          </Row>
          {field('email', 'Email Address', 'driver@example.com', 'email')}
          {field('phone', 'Mobile Number', '9876543210', 'tel')}
          {!verification.email.proof && <p className="small text-muted mb-3">Verify your email first to continue with mobile verification.</p>}
          {field('password', 'Password', 'Enter password', 'new-password')}
          {field('confirmPassword', 'Confirm Password', 'Confirm password', 'new-password')}
          <Button type="submit" className="w-100" disabled={busy || !verification.email.proof || !verification.phone.proof}>
            {loading ? 'Creating account...' : verification.email.proof && verification.phone.proof ? (isDriver ? 'Sign Up as Driver' : 'Sign Up') : 'Verify email and mobile to sign up'}
          </Button>
        </Form>
        <div className="social-divider"><span>or continue with</span></div>
        <div className="d-grid gap-2 mb-3"><GoogleSignInButton onClick={continueWithGoogle} disabled={busy} /></div>
        <p className="text-center mt-3">Already have an account? <Link to={loginRoute}>{isDriver ? "Driver Login" : "Login"}</Link></p>
        <p className="text-center"><Link to={isDriver ? ROUTES.signup : ROUTES.driverSignup}>{isDriver ? "Customer sign-up" : "Sign Up as Driver"}</Link></p>
      </div>
    </div>
  </Col></Row></Container></div>;
};
export default SignupForm;
